/*
 * Simple table in which the caller defines columns as objects that can render cells of that column at each
 * row.
 */
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactElement, ReactNode, useMemo} from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {Pagination} from 'lib/Listings';
import {makeReactContext} from 'lib/makeReactContext';
import {ZINDEX_SIMPLE_TABLE_FROZEN_HEADER} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import useHasBeenInView from 'ui-library/hooks/useHasBeenInView';
import useHoverState, {HoverStateProps} from 'ui-library/hooks/useHoverState';
import ResultsPager from 'ui-library/ResultsPager';
import {assertNever, ElementMouseEvent} from 'util/Types';

// Keep synced with simple-table.less > .simpleTable-header > background-color
const HEADER_BACKGROUND_COLOR = GRAY_LIGHTER;

// Functions passed into the components defined in this file that render table headers/cells
// should return strings, numbers, null, undefined, or a Cell.
type CellElement = string | number | null | undefined | ReactElement<CellProps>;

interface TableContextType {
  isCompact: boolean;
}

const TableContext = makeReactContext<TableContextType>(
  () => ({
    isCompact: false,
    showBorder: true,
  }),
  'TableContext'
);

export enum CellType {
  BODY = 'BODY',
  HEADER = 'HEADER',
  INLINE_HEADER = 'INLINE_HEADER',
  FROZEN_HEADER = 'FROZEN_HEADER',
}

/**
 * We wrap the header and body of SimpleTable in a CellTypeContext rather than trying to pass the cellType
 * down to Cells as props because consumers of SimpleTable may pass in components that wrap Cell, rather than
 * direct Cell components. For example:
 *   function EnhancedHeader(): JSX.Element {
 *     return <Cell>Fancy Header</Cell>;
 *   }
 *
 *   <Table><Column header={<EnhancedHeader />} /></Table>
 *
 * We do not want to require that all Cell-wrapping components take in and pass down cellType as a prop.
 * However we also need access to cellType within the Cell component. The solution is to use a context.
 */
interface CellTypeContextType {
  cellType: CellType;
}

const CellTypeContext = makeReactContext<CellTypeContextType>(
  () => ({cellType: CellType.BODY}),
  'CellTypeContext'
);

function _getCellNode(cell: CellElement): ReactElement<CellProps> {
  return !cell || typeof cell === 'string' || typeof cell === 'number' ? <Cell>{cell}</Cell> : cell;
}

interface HeaderCellProps {
  cellType: CellType.HEADER | CellType.FROZEN_HEADER;
  children?: ReactNode;
  className?: string;
  hoverProps?: HoverStateProps;
  onClick?: (e: ElementMouseEvent) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
  ariaSort?: 'ascending' | 'descending' | 'none' | 'other';
}

function HeaderCell(props: HeaderCellProps): JSX.Element {
  return (
    <TableContext.Consumer>
      {({isCompact}) => (
        <th
          className={classNames(
            getClassNameForCellType(props.cellType),
            props.className,
            css(isCompact && simpleTableStyles.compactHeaderCell)
          )}
          onClick={props.onClick}
          onMouseDown={props.onMouseDown}
          aria-sort={props.ariaSort}
          {...props.hoverProps}
          {...getTestProps({component: 'SimpleTable', element: 'headerCell'})}
        >
          {props.children}
        </th>
      )}
    </TableContext.Consumer>
  );
}

interface BodyCellProps {
  cellType: CellType.BODY | CellType.INLINE_HEADER;
  children?: ReactNode;
  className?: string;
  hoverProps?: HoverStateProps;
  onClick?: (e: ElementMouseEvent) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
}

function getClassNameForCellType(cellType: CellType): Array<string> {
  switch (cellType) {
    case CellType.BODY:
      return ['simpleTable-cell'];
    case CellType.HEADER:
      return ['simpleTable-header-cell'];
    case CellType.FROZEN_HEADER:
      return ['simpleTable-header-cell', css(simpleTableStyles.frozenHeader)];
    case CellType.INLINE_HEADER:
      return ['simpleTable-inline-header-cell'];
    default:
      throw new Error(`Invalid cellType: ${assertNever(cellType)}`);
  }
}

function BodyCell(props: BodyCellProps): JSX.Element {
  return (
    <TableContext.Consumer>
      {({isCompact}) => (
        <td
          className={classNames(
            getClassNameForCellType(props.cellType),
            props.className,
            css(isCompact && simpleTableStyles.compactCell)
          )}
          onClick={props.onClick}
          onMouseDown={props.onMouseDown}
          {...props.hoverProps}
          {...getTestProps({component: 'SimpleTable', element: 'dataCell'})}
        >
          {props.children}
        </td>
      )}
    </TableContext.Consumer>
  );
}

export interface CellProps {
  children?: ReactNode;
  className?: string;
  // used to create headers at other rows, see Effective Permissions Modal
  cellTypeOverride?: CellType;
  hoverProps?: HoverStateProps;
  onClick?: (e: ElementMouseEvent) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
  ariaSort?: 'ascending' | 'descending' | 'none' | 'other';

  // Note: If you add an event handler prop, make sure you consider how it interacts with HoverStateProps
}

export function Cell(props: CellProps): JSX.Element {
  return (
    <CellTypeContext.Consumer>
      {({cellType}) => {
        const cellTypeWithDefault = props.cellTypeOverride || cellType;
        return cellTypeWithDefault === CellType.HEADER || cellTypeWithDefault === CellType.FROZEN_HEADER ? (
          <HeaderCell {...props} cellType={cellTypeWithDefault} />
        ) : (
          <BodyCell {...props} cellType={cellTypeWithDefault} />
        );
      }}
    </CellTypeContext.Consumer>
  );
}

export interface CellRenderOptions {
  // hasRowBeenInView property will be:
  // - undefined if `calculateInView = false` is passed to Table
  // - true if `calculateInView = true` and the cell's row has been in the user's viewport at some point
  // - false if `calculateInView = false` and the cell's row has never been in the user's viewport
  // Note that if `calculateInView = true`, `hasRowBeenInView` will always be false the first time
  // renderCell is invoked, regardless of the visibility of the cell's row. If the row is visible,
  // renderCell will be called again with hasRowBeenInView = true on the row's second render. This is because
  // useHasBeenInView needs to wait until a ref has been assigned to an element before determining if it is
  // in view using the IntersectionObserver API.
  hasRowBeenInView?: boolean;
  isRowHovered: boolean;
}

interface ColumnProps {
  header?: CellElement;
  /** (props) => cell component */
  renderCell: (rowIndex: number, options: CellRenderOptions) => CellElement;
}

export function Column(props: ColumnProps): JSX.Element | null {
  // WARNING: This is not meant to be rendered. See unit tests for usage.
  return null;
}

interface EmptyTableRowProps {
  columns: Array<ReactNode>;
  emptyTableText?: ReactNode;
}

function EmptyTableRow(props: EmptyTableRowProps): JSX.Element {
  return (
    <TableContext.Consumer>
      {({isCompact}) => (
        <tr
          {...getTestProps({component: 'SimpleTable', element: 'emptyRow'})}
          className={classNames(
            'simpleTable-row',
            'simpleTable-row--empty',
            css(isCompact && simpleTableStyles.compactRow)
          )}
        >
          <td colSpan={props.columns.length} className="simpleTable-cell u-faded">
            {props.emptyTableText}
          </td>
        </tr>
      )}
    </TableContext.Consumer>
  );
}

interface DataRowProps {
  calculateInView: boolean;
  columns: Array<ReactElement<ColumnProps>>;
  rowIndex: number;
  className?: string;
  onClick?: (rowIndex: number, e: ElementMouseEvent) => void;
}

function DataRow(props: DataRowProps): JSX.Element {
  const {hasBeenInView: hasRowBeenInView, ref} = useHasBeenInView(props.calculateInView);
  const [isRowHovered, hoverProps] = useHoverState();

  return (
    <TableContext.Consumer>
      {({isCompact}) => (
        <tr
          ref={ref}
          className={classNames(
            'simpleTable-row',
            props.rowIndex === 0 && 'simpleTable-row--noShadow',
            props.className,
            css(isCompact && simpleTableStyles.compactRow)
          )}
          onClick={(e) => {
            if (props.onClick) {
              props.onClick(props.rowIndex, e);
            }
          }}
          {...hoverProps}
          {...getTestProps({component: 'SimpleTable', element: 'dataRow', index: `${props.rowIndex}`})}
        >
          {props.columns.map((column, columnIndex) => {
            const cell = _getCellNode(
              column.props.renderCell(props.rowIndex, {
                hasRowBeenInView,
                isRowHovered,
              })
            );
            return React.cloneElement(cell, {key: columnIndex});
          })}
        </tr>
      )}
    </TableContext.Consumer>
  );
}

interface HeaderProps {
  columns: Array<ReactElement<ColumnProps>>;
  headerClassName?: string;
  headerCellClassName?: string;
}

function Header(props: HeaderProps): JSX.Element {
  return (
    <TableContext.Consumer>
      {({isCompact}) => (
        <thead>
          <tr
            className={classNames(
              'simpleTable-header',
              props.headerClassName,
              css(isCompact && simpleTableStyles.compactHeader)
            )}
            {...getTestProps({component: 'SimpleTable', element: 'headerRow'})}
          >
            {props.columns.map((column, columnIndex) => {
              const el = _getCellNode(column.props.header);
              return React.cloneElement(el, {
                key: columnIndex,
                className: classNames(el.props.className, props.headerCellClassName),
              });
            })}
          </tr>
        </thead>
      )}
    </TableContext.Consumer>
  );
}

interface FooterProps {
  footer: ReactNode;
  columnCount: number;
}

function Footer(props: FooterProps): JSX.Element {
  return (
    <tfoot {...getTestProps({component: 'SimpleTable', element: 'footer'})}>
      <tr className="simpleTable-footer">
        <td colSpan={props.columnCount} className="simpleTable-footer-cell">
          {props.footer}
        </td>
      </tr>
    </tfoot>
  );
}

export interface TableProps {
  // Optional (with default = false). If true, the renderCell function passed into each column will receive
  // a `hasRowBeenInView` boolean in the options object, which is set to true if the row that the cell is
  // in has been within the user's viewport at some point (using the IntersectionObserver API),
  // and false otherwise.
  // If calculateInView is false, hasRowBeenInView will always be `undefined`.
  //
  // The hasRowBeenInView argument can be used to render cheap placeholder components in large tables or
  // tables with expensive-to-render cells (i.e. cells with chips), so that the rendering work is delayed
  // until the user scrolls to the data.
  // TODO(BNCH-19748): Revisit this and potentially abstract out the "row wrapper" behavior to allow more
  // general usage less specific to useHasBeenInView.
  calculateInView?: boolean;
  children: ReactNode;
  className?: string;
  emptyTableText?: ReactNode;
  // Optional (with default = false), use when the table has overflowVisible prop set to true, and the parent div
  // controls the overflow. This allows the table wrapper to grow to the maximum width/height along with the
  // table content; otherwise, you might come across an issue where the table rows have max and overflowing
  // widths, but not the table's border, which adds an odd line in the middle of the table where it would end
  // if not overflowing.
  fitContent?: boolean;
  // Optional footer component that will be rendered below the table
  footer?: ReactNode;
  // Optional (with default = false) - used to freeze the table header.
  // Only use when table's overflowVisible prop is set to true.
  // NOTE: It's recommended to use showBorder=false when using this prop to avoid a minor visual glitch
  // when scrolling in a table with overflow where the header will move up a pixel when beginning to scroll.
  frozenHeader?: boolean;
  getRowKey?: (rowIndex: number) => React.Key;
  includeNegativeMargins?: boolean;
  isCompact?: boolean;
  numRows: number;
  getRowClassName?: (rowIndex: number) => string;
  overflowVisible?: boolean;
  // Optional (with default), shows loading state
  isLoading?: boolean;
  onRowClick?: (rowIndex: number, e: ElementMouseEvent) => void;
  // Optional pagination props - shows pager
  pagination?: Pagination | null;
  message?: ReactNode; // Optional message displayed on the right side of the table
  setOffset?: (offset: number) => void;
  // Show 1px border around table; defaults to true.
  showBorder?: boolean;
  tableLayout?: 'fixed' | 'auto';
  classNames?: {
    header?: string;
    headerCell?: string;
    row?: string;
  };
}

export function Table({
  calculateInView = false,
  children,
  className,
  emptyTableText,
  fitContent = false,
  footer,
  frozenHeader = false,
  getRowKey = (rowIndex: number) => rowIndex,
  includeNegativeMargins = false,
  isCompact = false,
  numRows,
  getRowClassName,
  overflowVisible = false,
  isLoading = false,
  onRowClick,
  pagination,
  message,
  setOffset,
  showBorder = true,
  tableLayout = 'auto',
  classNames: tableClassNames,
}: TableProps): JSX.Element {
  assert(
    (fitContent && overflowVisible) || !fitContent,
    'Expected to also have overflowVisible prop set to true when fitContent prop is true'
  );
  assertAndContinue(
    (frozenHeader && overflowVisible) || !frozenHeader,
    'Expected to also have overflowVisible prop set to true when frozenHeader prop is true'
  );
  const columnChildren = React.Children.toArray(children) as Array<ReactElement<ColumnProps>>;
  const tableClassName = classNames('simpleTable', className, {
    'simpleTable--negativeMargins': includeNegativeMargins,
    'simpleTable--overflowVisible': overflowVisible,
    'simpleTable--bordered': showBorder,
  });
  const table = (
    <div
      className={classNames(tableClassName, {'u-flex-grow': fitContent})}
      {...getTestProps({component: 'SimpleTable', element: 'table'})}
    >
      <table
        className={classNames(
          'simpleTable-table',
          css(tableLayout === 'auto' && simpleTableStyles.tableLayoutAuto),
          css(tableLayout === 'fixed' && simpleTableStyles.tableLayoutFixed)
        )}
      >
        {columnChildren.some((column) => column.props.header) && (
          <CellTypeContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
            value={{cellType: frozenHeader ? CellType.FROZEN_HEADER : CellType.HEADER}}
          >
            <Header
              columns={columnChildren}
              headerClassName={tableClassNames?.header}
              headerCellClassName={tableClassNames?.headerCell}
            />
          </CellTypeContext.Provider>
        )}
        <tbody>
          {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing */}
          <CellTypeContext.Provider value={{cellType: CellType.BODY}}>
            {numRows === 0 ? (
              <EmptyTableRow columns={columnChildren} emptyTableText={emptyTableText} />
            ) : (
              _.range(numRows).map((rowIndex) => (
                <DataRow
                  key={getRowKey(rowIndex) ?? rowIndex}
                  calculateInView={calculateInView}
                  className={classNames(tableClassNames?.row, getRowClassName && getRowClassName(rowIndex))}
                  columns={columnChildren}
                  onClick={onRowClick}
                  rowIndex={rowIndex}
                />
              ))
            )}
          </CellTypeContext.Provider>
        </tbody>
        {footer && <Footer footer={footer} columnCount={columnChildren.length} />}
      </table>
    </div>
  );
  // Memoize the context to prevent re-renders
  const tableContext = useMemo(() => ({isCompact}), [isCompact]);
  return (
    <TableContext.Provider value={tableContext}>
      <div
        className={classNames({'u-flex-display': fitContent})}
        {...getTestProps({component: 'SimpleTable', element: 'root'})}
      >
        {pagination && (
          <ResultsPager isLoading={isLoading} setOffset={setOffset ?? (() => {})} pagination={pagination} />
        )}
        {message && <span className="faded pull-right">{message}</span>}
        {table}
      </div>
    </TableContext.Provider>
  );
}

export const simpleTableStyles = StyleSheet.create({
  compactHeader: {
    height: '36px',
  },
  compactHeaderCell: {
    padding: '10px 8px',
  },
  compactRow: {
    height: '28px',
  },
  compactCell: {
    padding: '6px 8px',
  },
  frozenHeader: {
    position: 'sticky',
    top: 0,
    // Without backgroundColor set, the header cells are transparent and you can
    // see content scrolling behind them.
    backgroundColor: HEADER_BACKGROUND_COLOR,
    // Without zIndex set, non-static-positioned content in table rows will be rendered over the table header.
    zIndex: ZINDEX_SIMPLE_TABLE_FROZEN_HEADER,
    // Adds a "border" to the bottom of the sticky header that remains when the table is scrolled.
    boxShadow: `inset 0px -1px 0px 0px ${GRAY_LIGHT}`,
  },
  grayHoverHeaderCell: {
    transition: 'background-color 0.1s',
    // Keep synced with simple-table.less > .simpleTable-header > background-color
    backgroundColor: HEADER_BACKGROUND_COLOR,
    ':hover': {
      backgroundColor: GRAY_LIGHT,
    },
  },
  tableLayoutAuto: {
    tableLayout: 'auto',
  },
  tableLayoutFixed: {
    tableLayout: 'fixed',
    // Fixed layout only works with set width. The component's minWidth is always set to 100%, so this should
    // not change usage.
    width: '100%',
  },
});
